/**
 * KPI sample periods (decision 0045). A sample's `at` names the period it describes; two samples are the same period
 * when their `at` rounds down to the same start of the KPI's cadence, in UTC: the hour (hourly), the day (daily), the
 * ISO week's Monday (weekly). `recordKpiSample` stores the rounded start, so a correction recorded at Tue 09:00 for the
 * week of Mon 00:00 replaces the first sample instead of adding a second point.
 */

export type KpiCadence = "hourly" | "daily" | "weekly"

const HOUR = 3_600_000
const DAY = 86_400_000

/** The latest `at` a sample may name: the largest time a Date can hold (Date's range, ±8.64e15 ms). */
export const KPI_AT_MAX = 8_640_000_000_000_000

/** The start (UTC) of the cadence period that `at` falls in. `at` is a non-negative epoch-millis integer; so is the result. */
export const kpiPeriodStart = (cadence: KpiCadence, at: number): number => {
  switch (cadence) {
    case "hourly":
      return Math.floor(at / HOUR) * HOUR
    case "daily":
      return Math.floor(at / DAY) * DAY
    case "weekly": {
      const day = Math.floor(at / DAY)
      // 1970-01-01 was a Thursday: (day + 3) mod 7 is the number of days since Monday. The week of the epoch starts
      // before it; its samples keep 0 (a sample's `at` is non-negative).
      return Math.max(0, (day - ((day + 3) % 7)) * DAY)
    }
  }
}

/** The period's label for a tool result: "2026-10-05 14:00 UTC" (hourly), "2026-10-05" (daily), "the week of 2026-10-05". */
export const kpiPeriodLabel = (cadence: KpiCadence, start: number): string => {
  const iso = new Date(start).toISOString()
  switch (cadence) {
    case "hourly":
      return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`
    case "daily":
      return iso.slice(0, 10)
    case "weekly":
      return `the week of ${iso.slice(0, 10)}`
  }
}

/** A KPI value with its unit: `41%` for "%", else `41 users` (no unit: `41`). */
export const kpiValueText = (kpi: { readonly unit: string }, value: number): string =>
  kpi.unit === "%" ? `${value}%` : kpi.unit.trim() === "" ? `${value}` : `${value} ${kpi.unit}`
